import { Button, Drawer, Flex } from '@mantine/core';
import { CellSort, DataTable } from '@shared/ui';
import { useMemo, useState, useCallback, useRef } from 'react';
import {
  type VacancyStatusesHistory,
  useGetVacancyStatusesHistory,
} from '@entities/vacancy';
import dayjs from 'dayjs';
import { VacancyStatusBadge } from '@widgets/vacancy';
import { useParams } from 'react-router';
import { type SortQuery } from '@shared/types';
import { PAGINATION_DEFAULTS } from '@shared/constants';

type VacancyActivityDrawerProps = {
  opened: boolean;
  close: () => void;
  statusesHistory: VacancyStatusesHistory[];
};

export const VacancyAcivityDrawer = ({
  opened,
  close,
  statusesHistory,
}: VacancyActivityDrawerProps) => {
  const { id } = useParams();
  const [sort, setSort] = useState<SortQuery>({
    createdAt: 'desc',
  });
  const scrollViewportRef = useRef<HTMLDivElement>(null);

  const {
    data,
    isLoading,
    isFetching,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetVacancyStatusesHistory(Number(id), {
    filter: {},
    sort,
    perPage: PAGINATION_DEFAULTS.PER_PAGE,
    page: PAGINATION_DEFAULTS.INITIAL_PAGE,
  });

  const handleSortChange = useCallback(
    (field: string, direction?: 'asc' | 'desc') => {
      if (!direction) {
        setSort(prevSort => {
          const newSort = { ...prevSort };
          delete newSort[field];
          return newSort;
        });
        return;
      }

      setSort({
        [field]: direction,
      });
    },
    []
  );

  const columns = useMemo(
    () => [
      {
        accessor: 'isActive',
        title: 'Активность',
        render: ({ newStatus }: VacancyStatusesHistory) => (
          <VacancyStatusBadge status={newStatus} />
        ),
      },
      {
        accessor: 'createdAt',
        title: (
          <CellSort
            label='Дата создания'
            onSort={dir => handleSortChange('createdAt', dir)}
            currentDirection={sort.createdAt}
          />
        ),
        render: ({ createdAt }: { createdAt: string }) =>
          dayjs(createdAt).format('DD.MM.YYYY HH:mm'),
      },
      {
        accessor: 'user.name',
        title: 'Сотрудник',
        render: ({ user }: VacancyStatusesHistory) => {
          const { firstName, lastName } = user?.profile || {};

          if (!firstName || !lastName) {
            return 'System';
          }

          return `${user?.profile.firstName} ${user?.profile.lastName}`;
        },
      },
    ],
    [statusesHistory.length, sort, handleSortChange]
  );

  const loadMoreRecords = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return (
    <Drawer
      opened={opened}
      onClose={close}
      title='История активности'
      size='lg'
      position='right'
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          justifyContent: 'space-between',
          overflowY: 'hidden',
        },
      }}
      keepMounted
    >
      <DataTable
        columns={columns}
        records={data?.data || []}
        fetching={isLoading || isFetching || isFetchingNextPage}
        onScrollToBottom={loadMoreRecords}
        scrollViewportRef={scrollViewportRef}
        height='100%'
      />

      <Flex mt='auto'>
        <Button variant='outline' onClick={close} mt={24}>
          Назад
        </Button>
      </Flex>
    </Drawer>
  );
};
